import type { FC } from 'react';
import { useState } from 'react';
import { Button, Form, type FormProps, Input, Modal, Switch } from 'antd';
import { createCategory } from '@/services/api/categoryService';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';

type FieldType = {
  name?: string;
  enabled?: boolean;
  default?: boolean;
};

interface ModalAddCategoryProps {
  open: boolean;
  onOk: () => void;
  onCancel: () => void;
  onLoadData: () => void;
}

const ModalAddCategory: FC<ModalAddCategoryProps> = ({
  open,
  onOk,
  onCancel,
  onLoadData,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onFinish: FormProps<FieldType>['onFinish'] = async values => {
    setIsLoading(true);
    const payload = {
      name: values.name || '',
      enabled: values.enabled ?? true,
      default: values.default ?? false,
    };

    const { success, error } = await createCategory(payload);
    if (!success) {
      message.error(error || t('home.categories.modals.add.error'));
      setIsLoading(false);
      return;
    }

    form.resetFields();
    setIsLoading(false);
    onLoadData();
    onCancel();
  };

  return (
    <Modal
      title={t('home.categories.modals.add.title')}
      open={open}
      destroyOnClose
      footer={
        <Button
          type="primary"
          onClick={() => form.submit()}
          loading={isLoading}
        >
          {t('home.categories.modals.add.submit')}
        </Button>
      }
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        initialValues={{ enabled: true, default: false }}
      >
        <Form.Item
          name="name"
          label={t('home.categories.modals.add.nameLabel')}
          rules={[
            {
              required: true,
              message: t('home.categories.modals.add.validationRequired'),
            },
            {
              min: 1,
              max: 255,
              message: t('home.categories.modals.add.validationLength'),
            },
          ]}
        >
          <Input
            placeholder={t('home.categories.modals.add.namePlaceholder')}
          />
        </Form.Item>

        <Form.Item name="enabled" valuePropName="checked">
          <Switch
            checkedChildren={t('home.categories.modals.add.enabled')}
            unCheckedChildren={t('home.categories.modals.add.disabled')}
          />
        </Form.Item>

        <Form.Item name="default" valuePropName="checked" hidden>
          <Switch
            checkedChildren={t('home.categories.modals.add.setDefault')}
            unCheckedChildren={t('home.categories.modals.add.notDefault')}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ModalAddCategory;
